import { UseChartDataProps, UseChartDataReturn } from '../types';
import { generatePowerProfileData } from '../utils';
import { useMemo } from 'react';

/**
 * Custom hook for managing chart data
 *
 * Features:
 * - Uses external data if available
 * - Generates data from parameters if external data is empty
 * - Validates required data for generation
 *
 * @param props - Configuration for chart data
 * @returns Object with chart data and validation status
 */
export const useChartData = ({
  data,
  peakPowerKw,
  averagePowerKw,
  workingDays,
  workingHoursStart,
  workingHoursEnd,
}: UseChartDataProps): UseChartDataReturn => {
  // Check if we have all required data to generate the chart
  const hasRequiredData = useMemo(() => {
    return Boolean(
      peakPowerKw && averagePowerKw && workingDays && workingDays.length > 0 && workingHoursStart && workingHoursEnd
    );
  }, [peakPowerKw, averagePowerKw, workingDays, workingHoursStart, workingHoursEnd]);

  // Calculate chart data
  const chartData = useMemo(() => {
    return data.length > 0
      ? data
      : hasRequiredData
        ? generatePowerProfileData(peakPowerKw!, averagePowerKw!, workingDays!, workingHoursStart!, workingHoursEnd!)
        : [];
  }, [data, hasRequiredData, peakPowerKw, averagePowerKw, workingDays, workingHoursStart, workingHoursEnd]);

  return {
    chartData,
    hasRequiredData,
  };
};
